import { Info, RotateCcw } from "lucide-react";
import { useCallback } from "react";
import { useSearchParams } from "react-router-dom";

import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { reportMeta } from "./reportMeta";

/**
 * Shared report-page chrome: every report tab gets the same header (title,
 * one-line "what this answers" explainer from REPORT_META, optional
 * "How this is calculated" popover) and the same filter row (date range +
 * tab-specific scope selects + Reset). Filter state lives in the URL so any
 * filtered view is shareable and Quick Answers can deep-link into it.
 */

// --- URL-backed filter state -------------------------------------------------

export function useReportFilters(defaults: Record<string, string>) {
  const [params, setParams] = useSearchParams();
  const get = useCallback(
    (key: string) => params.get(key) ?? defaults[key] ?? "",
    [params, defaults],
  );
  const set = useCallback(
    (key: string, value: string) => {
      const next = new URLSearchParams(params);
      if (value === "" || value === defaults[key]) next.delete(key);
      else next.set(key, value);
      setParams(next, { replace: true });
    },
    [params, setParams, defaults],
  );
  const reset = useCallback(() => {
    const next = new URLSearchParams(params);
    for (const key of Object.keys(defaults)) next.delete(key);
    setParams(next, { replace: true });
  }, [params, setParams, defaults]);
  const isDefault = Object.keys(defaults).every(
    (k) => (params.get(k) ?? defaults[k]) === defaults[k],
  );
  return { get, set, reset, isDefault };
}

// --- Date ranges -------------------------------------------------------------

export const DATE_RANGES = [
  { id: "30d", label: "Last 30 days" },
  { id: "90d", label: "Last 90 days" },
  { id: "quarter", label: "This quarter" },
  { id: "year", label: "This year" },
  { id: "all", label: "All time" },
] as const;

export type DateRangeId = (typeof DATE_RANGES)[number]["id"];

/** Start date for a range id; null = no lower bound. */
export function rangeStart(id: string): Date | null {
  const now = new Date();
  switch (id) {
    case "30d":
      return new Date(now.getTime() - 30 * 864e5);
    case "90d":
      return new Date(now.getTime() - 90 * 864e5);
    case "quarter": {
      const q = Math.floor(now.getMonth() / 3) * 3;
      return new Date(now.getFullYear(), q, 1);
    }
    case "year":
      return new Date(now.getFullYear(), 0, 1);
    default:
      return null;
  }
}

export const DateRangeSelect = ({
  value,
  onChange,
}: {
  value: string;
  onChange: (v: string) => void;
}) => (
  <Select value={value} onValueChange={onChange}>
    <SelectTrigger className="h-8 w-40 text-sm">
      <SelectValue />
    </SelectTrigger>
    <SelectContent>
      {DATE_RANGES.map((r) => (
        <SelectItem key={r.id} value={r.id}>
          {r.label}
        </SelectItem>
      ))}
    </SelectContent>
  </Select>
);

// --- Header + filter bar -----------------------------------------------------

export const ReportPageHeader = ({
  slug,
  methodology,
  children,
}: {
  slug: string;
  methodology?: React.ReactNode;
  children?: React.ReactNode;
}) => {
  const meta = reportMeta(slug);
  return (
    <div className="flex flex-col gap-3">
      <div>
        <div className="flex items-center gap-2">
          <h2 className="text-lg font-semibold">{meta.label}</h2>
          {methodology && (
            <Popover>
              <PopoverTrigger asChild>
                <button
                  type="button"
                  aria-label="How this is calculated"
                  className="text-muted-foreground hover:text-foreground"
                >
                  <Info className="w-4 h-4" />
                </button>
              </PopoverTrigger>
              <PopoverContent className="w-80 text-sm" align="start">
                <p className="text-[11px] uppercase tracking-wide text-muted-foreground font-medium mb-1.5">
                  How this is calculated
                </p>
                {methodology}
              </PopoverContent>
            </Popover>
          )}
        </div>
        <p className="text-sm text-muted-foreground">{meta.description}</p>
      </div>
      {children}
    </div>
  );
};

export const ReportFilterBar = ({
  children,
  onReset,
  showReset,
}: {
  children: React.ReactNode;
  onReset?: () => void;
  showReset?: boolean;
}) => (
  <div className="flex flex-wrap items-center gap-2">
    {children}
    {onReset && showReset && (
      <Button variant="ghost" size="sm" className="h-8 text-muted-foreground" onClick={onReset}>
        <RotateCcw className="w-3.5 h-3.5" /> Reset
      </Button>
    )}
  </div>
);
